import React, { useState, useEffect } from "react";
import {
  useCreateModuleMutation,
  useUpdateModuleMutation,
} from "../../../store/auth/authApi";
import { Loader } from "../../../components";

export default function ModuleModal({
  isOpen,
  onClose,
  module = null,
  onSuccess,
}) {
  const [formData, setFormData] = useState({
    name: "",
    url: "",
  });
  const [apiError, setApiError] = useState("");

  const [createModule, { isLoading: isCreating }] = useCreateModuleMutation();
  const [updateModule, { isLoading: isUpdating }] = useUpdateModuleMutation();

  const isEdit = !!module;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (module) {
      setFormData({
        name: module.name || "",
        url: module.url || "",
      });
    } else {
      setFormData({
        name: "",
        url: "",
      });
    }
    // Clear any previous errors when modal opens
    setApiError("");
  }, [module]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear API error when user starts typing
    if (apiError) {
      setApiError("");
    }

    // Format URL to allow letters, numbers, underscores, and hyphens
    let formattedValue = value;
    if (name === "url") {
      formattedValue = value
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, "")
        .replace(/[-_]{2,}/g, "_")
        .replace(/^[-_]+|[-_]+$/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setApiError("");

    try {
      if (isEdit) {
        await updateModule({
          id: module.id,
          ...formData,
        }).unwrap();
      } else {
        await createModule(formData).unwrap();
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving module:", error);
      console.error("Error details:", {
        status: error?.status,
        data: error?.data,
        message: error?.message,
        originalStatus: error?.originalStatus,
      });

      // Extract error message from API response
      let errorMessage = "An error occurred while saving the module";

      // Check for different error formats
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.error) {
        errorMessage = error.error;
      } else if (error?.status === 500) {
        errorMessage = "Server error occurred. Please try again later.";
      } else if (error?.status === 400) {
        errorMessage = "Invalid request. Please check your input.";
      }

      setApiError(errorMessage);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative z-[1001] shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {isEdit ? "Edit Module" : "Create New Module"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* API Error Display */}
          {apiError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{apiError}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Module Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Module Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A] focus:border-transparent"
              placeholder="Enter module name"
              required
            />
          </div>

          {/* Module URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Module URL
            </label>
            <input
              type="text"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A] focus:border-transparent"
              placeholder="module_url"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Letters, numbers, underscores, and hyphens are allowed
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-[#8B313A] text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <Loader
                    size="sm"
                    color="white"
                    variant="spinner"
                    className="mr-2"
                  />
                  {isEdit ? "Updating..." : "Creating..."}
                </>
              ) : isEdit ? (
                "Update Module"
              ) : (
                "Create Module"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
